import maxBy from 'lodash/maxBy'
import values from 'lodash/values'
import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import { ORDER_EVENT_STATUS_APPROVED } from '@klear/constants/order-event-status'
import type { Money } from '@klear/money'
import {
  documentReadiness,
  type DocumentReadiness,
  type OrderEventLifecycleState,
  type OrderEventLifecycleType
} from '@klear/order-event-lifecycle/lifecycle'
import { PurchaseOrder } from '@klear/purchase-order/purchase-order'

export type FundingDocument<Event> = { event?: Event; status: DocumentReadiness }

export type FundingDocuments<Invoice, PurchaseOrderEvent, Proof> = {
  purchaseOrder: FundingDocument<PurchaseOrderEvent> & { isPlaceholder: boolean }
  invoice: FundingDocument<Invoice>
  proofOfAcceptance: FundingDocument<Proof>
}

type StatusedEvent = { status: string }

type IdentifiedEvent = StatusedEvent & { id: number }

function fundingProof<P extends IdentifiedEvent>(proofs: readonly P[]): P | undefined {
  return (
    maxBy(
      proofs.filter((proof) => proof.status === ORDER_EVENT_STATUS_APPROVED),
      (proof) => proof.id
    ) ?? maxBy(proofs, (proof) => proof.id)
  )
}

function fundingDocument<Event extends StatusedEvent>(
  type: OrderEventLifecycleType,
  event: Event | undefined
): FundingDocument<Event> {
  if (event == null) return { status: 'missing' }
  return { event, status: documentReadiness(type, event.status as OrderEventLifecycleState) }
}

export function fundingDocuments<
  Invoice extends StatusedEvent,
  PurchaseOrderEvent extends StatusedEvent & { amount: Money; number?: string | null; notes?: string | null },
  ProofEvent extends IdentifiedEvent
>(input: {
  invoice?: Invoice
  purchaseOrder?: PurchaseOrderEvent
  proofs: readonly ProofEvent[]
}): FundingDocuments<Invoice, PurchaseOrderEvent, ProofEvent> {
  const isPlaceholder = input.purchaseOrder != null && PurchaseOrder.isPlaceholder(input.purchaseOrder)
  const proof = fundingProof(input.proofs)
  return {
    purchaseOrder: isPlaceholder
      ? { event: input.purchaseOrder, status: 'missing', isPlaceholder }
      : { ...fundingDocument(PERMISSION_ENTITY_PURCHASE_ORDER, input.purchaseOrder), isPlaceholder },
    invoice: fundingDocument(PERMISSION_ENTITY_INVOICE, input.invoice),
    proofOfAcceptance: fundingDocument(PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE, proof)
  }
}

export function fundingDocumentsNeedTrader(documents: FundingDocuments<unknown, unknown, unknown>): boolean {
  return values(documents).some((document) => document.status === 'missing' || document.status === 'waitingOnTrader')
}
